import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { TbChevronDown, TbCheck, TbSearch } from "react-icons/tb";
import { clsx } from "clsx";

interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SearchableSelectProps {
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  options: SelectOption[];
  disabled?: boolean;
  hasError?: boolean;
  className?: string;
  searchPlaceholder?: string;
}

export const SearchableSelect = React.forwardRef<
  HTMLButtonElement,
  SearchableSelectProps
>(
  (
    {
      value,
      onValueChange,
      placeholder = "Select",
      options,
      disabled = false,
      hasError = false,
      className,
      searchPlaceholder = "Search...",
    },
    ref
  ) => {
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [highlight, setHighlight] = useState(0);
    const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const listRef = useRef<HTMLDivElement | null>(null);

    React.useImperativeHandle(ref, () => triggerRef.current!, []);

    const selectedOption = useMemo(
      () => options.find((o) => o.value === value && o.value !== ""),
      [options, value]
    );

    const filteredOptions = useMemo(() => {
      const term = searchTerm.trim().toLowerCase();
      if (!term) return options;
      return options.filter((option) =>
        option.label.toLowerCase().includes(term)
      );
    }, [options, searchTerm]);

    useLayoutEffect(() => {
      if (!open) return;
      const update = () => {
        const rect = triggerRef.current?.getBoundingClientRect();
        if (!rect) return;
        setPos({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
          width: rect.width,
        });
      };
      update();
      window.addEventListener("scroll", update, true);
      window.addEventListener("resize", update);
      return () => {
        window.removeEventListener("scroll", update, true);
        window.removeEventListener("resize", update);
      };
    }, [open]);

    useEffect(() => {
      if (!open) return;
      const handlePointerDown = (e: MouseEvent) => {
        const target = e.target as Node;
        if (
          triggerRef.current?.contains(target) ||
          dropdownRef.current?.contains(target)
        ) {
          return;
        }
        setOpen(false);
      };
      document.addEventListener("mousedown", handlePointerDown);
      return () => document.removeEventListener("mousedown", handlePointerDown);
    }, [open]);

    useEffect(() => {
      if (!open) return;
      const id = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }, [open]);

    useEffect(() => {
      if (!open) {
        setSearchTerm("");
        return;
      }
      const idx = filteredOptions.findIndex((o) => o.value === value);
      setHighlight(idx >= 0 ? idx : 0);
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      if (highlight >= filteredOptions.length) {
        setHighlight(filteredOptions.length > 0 ? filteredOptions.length - 1 : 0);
      }
    }, [filteredOptions.length, highlight]);

    useEffect(() => {
      if (!open) return;
      const node = listRef.current?.querySelector<HTMLElement>(
        `[data-index="${highlight}"]`
      );
      node?.scrollIntoView({ block: "nearest" });
    }, [highlight, open]);

    const commitValue = useCallback(
      (next: string, isDisabled?: boolean) => {
        if (isDisabled) return;
        onValueChange?.(next);
        setOpen(false);
      },
      [onValueChange]
    );

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlight((h) => {
          for (let i = h + 1; i < filteredOptions.length; i++) {
            if (!filteredOptions[i].disabled) return i;
          }
          return h;
        });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlight((h) => {
          for (let i = h - 1; i >= 0; i--) {
            if (!filteredOptions[i].disabled) return i;
          }
          return h;
        });
      } else if (e.key === "Home") {
        e.preventDefault();
        const i = filteredOptions.findIndex((o) => !o.disabled);
        if (i >= 0) setHighlight(i);
      } else if (e.key === "End") {
        e.preventDefault();
        for (let i = filteredOptions.length - 1; i >= 0; i--) {
          if (!filteredOptions[i].disabled) {
            setHighlight(i);
            break;
          }
        }
      } else if (e.key === "Enter") {
        e.preventDefault();
        const opt = filteredOptions[highlight];
        if (opt) commitValue(opt.value, opt.disabled);
      } else if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
      } else if (e.key === "Tab") {
        setOpen(false);
      }
    };

    const handleTriggerKeyDown = (
      e: React.KeyboardEvent<HTMLButtonElement>
    ) => {
      if (disabled) return;
      if (
        e.key === "ArrowDown" ||
        e.key === "ArrowUp" ||
        e.key === "Enter" ||
        e.key === " "
      ) {
        e.preventDefault();
        setOpen(true);
      }
    };

    const triggerLabel = selectedOption?.label ?? placeholder;
    const showPlaceholder = !selectedOption;

    return (
      <>
        <button
          ref={triggerRef}
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-disabled={disabled}
          disabled={disabled}
          onClick={() => !disabled && setOpen((o) => !o)}
          onKeyDown={handleTriggerKeyDown}
          className={clsx(
            "flex w-full items-center justify-between text-sm ring-offset-background",
            "focus:outline-none",
            "disabled:cursor-not-allowed disabled:opacity-50",
            !className &&
              "h-10 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 focus:bg-white",
            hasError && "border-red-400",
            className
          )}
        >
          <span
            className={clsx(
              "truncate text-left",
              showPlaceholder && "text-gray-500"
            )}
          >
            {triggerLabel}
          </span>
          <TbChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>

        {open &&
          createPortal(
            <div
              ref={dropdownRef}
              role="listbox"
              style={{
                position: "absolute",
                top: pos.top,
                left: pos.left,
                width: pos.width,
              }}
              className="z-200000 max-h-96 overflow-hidden rounded-lg border border-gray-200 bg-white text-popover-foreground shadow-lg"
            >
              <div className="p-2 border-b border-gray-100">
                <div className="relative">
                  <TbSearch className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none"
                  />
                </div>
              </div>

              <div
                ref={listRef}
                className="max-h-72 overflow-y-auto p-1"
              >
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option, index) => {
                    const isSelected = option.value === value;
                    const isHighlighted = index === highlight;
                    return (
                      <div
                        key={`${option.value}-${index}`}
                        role="option"
                        aria-selected={isSelected}
                        aria-disabled={option.disabled}
                        data-index={index}
                        onMouseEnter={() => setHighlight(index)}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          commitValue(option.value, option.disabled);
                        }}
                        className={clsx(
                          "relative flex w-full cursor-default select-none items-center rounded-md py-2 pl-8 pr-2 text-sm outline-none",
                          isHighlighted && !option.disabled && "bg-gray-100",
                          option.disabled && "pointer-events-none opacity-50"
                        )}
                      >
                        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                          {isSelected && <TbCheck className="h-4 w-4" />}
                        </span>
                        <span className="truncate">{option.label}</span>
                      </div>
                    );
                  })
                ) : (
                  <div className="py-6 text-center text-sm text-gray-500">
                    No results found
                  </div>
                )}
              </div>
            </div>,
            document.body
          )}
      </>
    );
  }
);

SearchableSelect.displayName = "SearchableSelect";
